/**
 * Site-specific configuration.
 * This is the ONLY file that should differ between deployments (NYC vs SB vs others).
 * All other source files import from here instead of hardcoding site-specific values.
 */

export const siteConfig = {
  ctx: "nyc",
  defaults: {
    selectedId: 'MN0102',
    activeGeography: 'NTA',
    activeIndicator: 'ahdi',
    bivariateIndicator: 'food_insecure',
    moduleId: 'ahdi',
    activeModuleId: 'ahdi',
    activeSubCategory: 'American Human Development Index',
    cachedSelectedIds: {
      Boro: '1',
      CD: '101',
      PUMA: '',
      NTA: 'MN0102',
      Tract: '36047028600',
    } as Record<string, string>,
    cachedActiveIndicators: {
      Tract: { active: 'ahdi', bivariate: 'food_insecure' },
      CD: { active: 'ahdi', bivariate: 'food_insecure' },
      NTA: { active: 'ahdi', bivariate: 'food_insecure' },
      Boro: { active: 'ahdi', bivariate: 'food_insecure' },
    } as Record<string, { active: string; bivariate: string }>,
  },

  map: {
    center: [40.7031, -73.9712] as [number, number],
    zoom: 10,
    minZoom: 9,
    bounds: [
      [40.0358181900000005, -74.8665193700000060],
      [41.3165406899999965, -73.0809477399999992],
    ] as [[number, number], [number, number]],
  },

  geographies: {
    Boro: { enabled: true, label: 'Borough', shortLabel: 'Boro' },
    CD: { enabled: true, label: 'Community District', shortLabel: 'CD' },
    NTA: { enabled: true, label: 'Neighborhood (NTA)', shortLabel: 'NTA' },
    Tract: { enabled: true, label: 'Tract', shortLabel: 'Tract' },
    PUMA: { enabled: true, label: 'PUMA', shortLabel: 'PUMA' },
  } as Record<string, { enabled: boolean; label: string; shortLabel: string }>,

  nav: {
    demographicExplorer: true,
    dashboardLabel: 'NEIGHBORHOOD<br/>EXPLORER',
    overallLabel: 'NYC',
  },

  search: {
    geocodeContext: 'New York City, NY',
    geocodeBounds: {
      minLon: -74.25909,
      minLat: 40.477399,
      maxLon: -73.700181,
      maxLat: 40.917577,
    },
  },

  data: {
    sorter: {
      path: '/data/tabular/NYCDATA2GO_sorter.csv',
      suffixMap: {} as Record<string, string>,
      fieldMap: {} as Record<string, string>,
    },
    order: {
      path: '/data/tabular/ORDER_Stat_Explorer.csv',
    },
    orderStories: {
      path: '/data/tabular/ORDER_Data_Stories.csv',
    },
    orderDashboard: {
      path: '/data/tabular/ORDER_Dashboard.csv',
    },
    sorterDashboard: {
      path: '/data/tabular/NYCDATA2GO_sorter_dashboard.csv',
      suffixMap: {} as Record<string, string>,
      fieldMap: {} as Record<string, string>,
    },
    tract: {
      path: '/data/tabular/D2G_Flatfile_Tract.csv',
      suffixMap: {} as Record<string, string>,
      excludedGeoids: [],
    },
    nyc: {
      path: '/data/tabular/D2G_Flatfile_NYC.csv',
      suffixMap: {} as Record<string, string>,
    },
    cd: {
      path: '/data/tabular/D2G_Flatfile_CD.csv',
      suffixMap: {} as Record<string, string>,
    },
    topoJson: '/data/geo/ALL_GEO_2020.json',
    geoidLookup: true,
    geoTypeRemap: {} as Record<string, string>,
    tooltipNameOverrides: {} as Record<string, string>,
  },

  swarmCalibration: {
    Tract: {
      radius: 1,
      padding: 0.25,
      pointDiameter: 0.75,
      collisionDiameter: 0.17,
      stackCount: 35,
      testEachPoint: "FALSE",
    },
    CD: {
      radius: 6,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 1.3,
      stackCount: 45,
      testEachPoint: "FALSE",
    },
    CDRG: {
      radius: 1.5,
      padding: 1.5,
      pointDiameter: 2,
      collisionDiameter: 0.75,
      stackCount: 25,
      testEachPoint: "FALSE",
    },
    Boro: {
      radius: 12,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 3,
      stackCount: 3,
      testEachPoint: "FALSE",
    },
    BoroRG: {
      radius: (10 * .75) / 2,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 1.7,
      stackCount: 3,
      testEachPoint: "FALSE",
    },
    NTA: {
      radius: 4,
      padding: 0.5,
      pointDiameter: 2,
      collisionDiameter: 0.5,
      stackCount: 25,
      testEachPoint: "FALSE",
    },
    ScatterPlot: {
      radius: 3,
    },
    RangeSwarm: {
      Tract: {
        radius: 1,
        padding: 0.25,
        pointDiameter: 0.75,
        collisionDiameter: 0.22,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 35,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      CD: {
        radius: 6,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 1.35,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 45,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      CDRG: {
        radius: 1.5,
        padding: 1.5,
        pointDiameter: 2,
        collisionDiameter: 0.8,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 25,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      Boro: {
        radius: 12,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 3.25,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 3,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      BoroRG: {
        radius: (10 * .75) / 2,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 1.8,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 3,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
      NTA: {
        radius: 4,
        padding: 0.5,
        pointDiameter: 2,
        collisionDiameter: 0.55,
        intervalBufferPct: 1.5,
        rowSpacing: 0,
        stackCount: 25,
        testEachPoint: "FALSE",
        segmentLengthPct: 6,
      },
    },
  } as Record<string, any>,

  mobileIndicators:[
    "ahdi",
    "median_personal_earnings", 
    "edu_index", 
    "health_index", 
    "income_index", 
    "life_expectancy"
  ],

  mobileDisplays: {
    ahdi: "Human Development Index",
    median_personal_earnings: "Median Personal Earnings",
    edu_index: "Education Index",
    health_index: "Health Index",
    income_index: "Income Index",
    life_expectancy: "Life Expectancy"
  },

  storyTabs: [
    { key: 'Well-Being in NYC', label: 'Well-Being in NYC', type: 'interactive' },
    { key: 'Story pdf', label: 'Life Expectancy', type: 'pdf', url: '/D2G_MOA_Data-Story_Life-Expectancy_2025-04-29.pdf' },
  ] as { key: string; label: string; type: 'interactive' | 'pdf'; url?: string }[],

  defaultStory: 'Well-Being in NYC',
};
